import React from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import TruncatedTooltip from 'components/mui/Tooltip/TruncatedTooltip';
import { directoryUserName, type DirectoryUser } from '../../hooks/useChatUserDirectory';

interface UserPreviewItemProps {
  user: DirectoryUser;
  onClick: (user: DirectoryUser) => void;
  /** Disables the row while a chat creation is in progress (interaction lock). */
  disabled?: boolean;
}

// Mirror of ChannelPreviewItem.RowRoot's hover treatment but simplified: no selected/menu/manage
// states (a directory row is never "selected" and has no context menu). `styled` (computed once)
// over `sx` per src/components/CLAUDE.md — this is a list-row hot path. `shouldForwardProp` keeps
// `disabled` off the DOM (Emotion has no transient-prop syntax).
const RowRoot = styled('div', {
  label: 'UserPreviewRowRoot',
  shouldForwardProp: (prop) => prop !== 'disabled',
})<{ disabled: boolean }>(({ theme, disabled }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(2.375),
  padding: theme.spacing(1.5, 2),
  // 4px transparent left border matches ChannelPreviewItem so rows align vertically with chats.
  borderLeft: '4px solid transparent',
  // Shared bottom divider — identical color/width to chat rows and the empty "No chats" block so
  // the combined Chats + Users list reads as one continuously separated list (DA-19646, task 6.2).
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  pointerEvents: disabled ? 'none' : 'auto',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const AVATAR_SIZE = 56;

// Avatar fallback initials when there is no picture: first + last initial, uppercased.
const avatarInitials = (user: DirectoryUser): string =>
  `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase() || user.username.charAt(0).toUpperCase();

const UserPreviewItem: React.FC<UserPreviewItemProps> = ({ user, onClick, disabled = false }) => {
  // Shared with the directory's search/filter (useChatUserDirectory) so the row label and the
  // searchable name are always the same string — a name shown here always matches the search box.
  const name = directoryUserName(user);
  return (
    <RowRoot disabled={disabled} onClick={() => onClick(user)}>
      <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>
        <Avatar src={user.picture || undefined} sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}>
          {avatarInitials(user)}
        </Avatar>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
        <TruncatedTooltip title={name} placement="bottom" disableInteractive>
          <Typography
            variant="subtitle2"
            noWrap
            sx={(theme) => ({ minWidth: 0, lineHeight: '20px', color: theme.palette.text.primary })}
          >
            {name}
          </Typography>
        </TruncatedTooltip>
      </Box>
    </RowRoot>
  );
};

export default UserPreviewItem;
